import React, { useState, useEffect } from 'react';
import { get, post, put, del } from '../../utils/apiClient';
import Base64Image from '../Base64Image';
import {
  AdminSectionTitle,
  AdminSectionText,
  AdminLoginLabel,
  AdminLoginInput,
  AdminLoginButton,
  AdminButton,
  AdminSaveButton,
} from '../../styles/AdminPageStyles';

const AdminTestimoniesPage = () => {
  const [pageData, setPageData] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageSaving, setPageSaving] = useState(false);
  const [pageError, setPageError] = useState(null);

  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [postsError, setPostsError] = useState(null);
  const [pageSaveSuccess, setPageSaveSuccess] = useState(null);
  const [postsSaveSuccess, setPostsSaveSuccess] = useState(null);

  const [newPost, setNewPost] = useState({
    title: '',
    author: '',
    description: '',
    category: 'other',
    status: 'published',
    isFeatured: false,
  });
  const [savingNewPost, setSavingNewPost] = useState(false);

  const [editingPost, setEditingPost] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [imageUploadingId, setImageUploadingId] = useState(null);

  useEffect(() => {
    const loadPage = async () => {
      setPageLoading(true);
      setPageError(null);
      try {
        const data = await get('/testimonies');
        setPageData(data);
      } catch (err) {
        setPageError(err && err.message ? err.message : 'Failed to load testimonies page config.');
      } finally {
        setPageLoading(false);
      }
    };

    const loadPosts = async () => {
      setPostsLoading(true);
      setPostsError(null);
      try {
        const data = await get('/testimonies/posts');
        setPosts(Array.isArray(data) ? data : []);
      } catch (err) {
        setPostsError(err && err.message ? err.message : 'Failed to load testimony posts.');
      } finally {
        setPostsLoading(false);
      }
    };

    loadPage();
    loadPosts();
  }, []);

  const handleIntroChange = (e) => {
    const { value } = e.target;
    setPageData((prev) => ({
      ...(prev || {}),
      introText: value,
    }));
  };

  const handleCategoryChange = (index, field, value) => {
    setPageData((prev) => {
      const next = { ...(prev || {}) };
      const categories = Array.isArray(next.categories) ? [...next.categories] : [];
      if (!categories[index]) {
        categories[index] = {};
      } else {
        categories[index] = { ...categories[index] };
      }
      categories[index][field] = value;
      next.categories = categories;
      return next;
    });
  };

  const savePageConfig = async (e) => {
    e.preventDefault();
    if (!pageData) return;

    setPageSaving(true);
    setPageError(null);
    setPageSaveSuccess(null);

    try {
      const payload = {
        header: {
          ...(pageData.header || {}),
          title:
            (pageData.header && pageData.header.title) ||
            'Testimonies',
        },
        introText: pageData.introText || '',
        categories: Array.isArray(pageData.categories)
          ? pageData.categories
          : [],
      };

      const updated = await put('/testimonies', payload);
      setPageData(updated);
    } catch (err) {
      setPageError(err && err.message ? err.message : 'Failed to save page settings.');
    } finally {
      setPageSaving(false);
    }
  };

  const handleNewPostChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewPost((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const createPost = async (e) => {
    e.preventDefault();
    setPostsError(null);
    setPostsSaveSuccess(null);

    if (!newPost.description || !newPost.description.trim()) {
      setPostsError('Description is required.');
      return;
    }

    setSavingNewPost(true);
    try {
      await post('/testimonies/posts', newPost);
      const data = await get('/testimonies/posts');
      setPosts(Array.isArray(data) ? data : []);
      setNewPost({
        title: '',
        author: '',
        description: '',
        category: 'other',
        status: 'published',
        isFeatured: false,
      });
      setPostsSaveSuccess('Testimony created successfully.');
    } catch (err) {
      setPostsError(err && err.message ? err.message : 'Failed to create testimony.');
    } finally {
      setSavingNewPost(false);
    }
  };

  const startEditing = (postItem) => {
    setEditingPost({
      ...postItem,
      status: 'published',
    });
  };

  const handleEditChange = (e) => {
    if (!editingPost) return;
    const { name, value, type, checked } = e.target;
    setEditingPost((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editingPost || !editingPost._id) return;

    setPostsError(null);
    setPostsSaveSuccess(null);

    if (!editingPost.description || !editingPost.description.trim()) {
      setPostsError('Description is required.');
      return;
    }

    setSavingEdit(true);
    try {
      const payload = {
        title: editingPost.title,
        author: editingPost.author,
        description: editingPost.description,
        category: editingPost.category,
        status: editingPost.status,
        isFeatured: !!editingPost.isFeatured,
      };

      await put(`/testimonies/posts/${editingPost._id}`, payload);
      const data = await get('/testimonies/posts');
      setPosts(Array.isArray(data) ? data : []);
      setEditingPost(null);
      setPostsSaveSuccess('Testimony updated successfully.');
    } catch (err) {
      setPostsError(err && err.message ? err.message : 'Failed to update testimony.');
    } finally {
      setSavingEdit(false);
    }
  };

  const deletePost = async (id) => {
    if (!id) return;
    const ok = window.confirm('Delete this testimony?');
    if (!ok) return;

    setPostsError(null);
    setPostsSaveSuccess(null);
    try {
      await del(`/testimonies/posts/${id}`);
      const data = await get('/testimonies/posts');
      setPosts(Array.isArray(data) ? data : []);
      setPostsSaveSuccess('Testimony deleted successfully.');
    } catch (err) {
      setPostsError(err && err.message ? err.message : 'Failed to delete testimony.');
    }
  };

  const handleImageUpload = async (postId, file) => {
    if (!postId || !file) return;
    setImageUploadingId(postId);
    setPostsError(null);
    setPostsSaveSuccess(null);

    try {
      const formData = new FormData();
      formData.append('image', file);
      await post(`/testimonies/posts/${postId}/image`, formData);
      const data = await get('/testimonies/posts');
      setPosts(Array.isArray(data) ? data : []);
      setPostsSaveSuccess('Testimony image uploaded successfully.');
    } catch (err) {
      setPostsError(err && err.message ? err.message : 'Failed to upload image.');
    } finally {
      setImageUploadingId(null);
    }
  };

  return (
    <>
      <AdminSectionTitle>Testimonies CMS</AdminSectionTitle>

      <form onSubmit={savePageConfig} style={{ marginBottom: 24 }}>
        <AdminSectionText style={{ marginBottom: 8 }}>
          Update the introduction text and categories for the testimonies page.
        </AdminSectionText>

        <div style={{ marginBottom: 12 }}>
          <AdminLoginLabel htmlFor="introText">Intro Text</AdminLoginLabel>
          <textarea
            id="introText"
            rows={3}
            value={(pageData && pageData.introText) || ''}
            onChange={handleIntroChange}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
        </div>

        {pageData && Array.isArray(pageData.categories) && (
          <div style={{ marginBottom: 12 }}>
            <AdminSectionText style={{ marginBottom: 6 }}>
              Categories
            </AdminSectionText>
            {pageData.categories.map((category, index) => (
              <div
                key={category.key || index}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '120px 1fr',
                  gap: 8,
                  marginBottom: 6,
                }}
              >
                <input
                  type="text"
                  value={category.name || ''}
                  onChange={(e) => handleCategoryChange(index, 'name', e.target.value)}
                  placeholder="Name"
                  style={{ padding: 6, fontSize: '0.85rem' }}
                />
                <input
                  type="text"
                  value={category.description || ''}
                  onChange={(e) => handleCategoryChange(index, 'description', e.target.value)}
                  placeholder="Description (optional)"
                  style={{ padding: 6, fontSize: '0.85rem' }}
                />
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 32, display: 'flex', alignItems: 'center', gap: '16px' }}>
          <AdminSaveButton type="submit" disabled={pageLoading || pageSaving}>
            {pageSaving ? 'Saving...' : 'Save Page Settings'}
          </AdminSaveButton>
          {pageSaveSuccess && (
            <span style={{ color: '#1b5e20', fontWeight: '500' }}>{pageSaveSuccess}</span>
          )}
          {pageError && (
            <span style={{ color: '#b71c1c', fontWeight: '500' }}>{pageError}</span>
          )}
        </div>
      </form>

      <hr style={{ margin: '16px 0' }} />

      <AdminSectionTitle>New Testimony</AdminSectionTitle>

      <form onSubmit={createPost} style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 8 }}>
          <AdminLoginLabel htmlFor="new-title">Title</AdminLoginLabel>
          <AdminLoginInput
            id="new-title"
            name="title"
            value={newPost.title}
            onChange={handleNewPostChange}
          />
        </div>
        <div style={{ marginBottom: 8 }}>
          <AdminLoginLabel htmlFor="new-author">Author</AdminLoginLabel>
          <AdminLoginInput
            id="new-author"
            name="author"
            value={newPost.author}
            onChange={handleNewPostChange}
          />
        </div>
        <div style={{ marginBottom: 8 }}>
          <AdminLoginLabel htmlFor="new-description">Description</AdminLoginLabel>
          <textarea
            id="new-description"
            name="description"
            rows={3}
            value={newPost.description}
            onChange={handleNewPostChange}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <div style={{ flex: 1 }}>
            <AdminLoginLabel htmlFor="new-category">Category</AdminLoginLabel>
            <select
              id="new-category"
              name="category"
              value={newPost.category}
              onChange={handleNewPostChange}
              style={{ width: '100%', padding: 6, fontSize: '0.85rem' }}
            >
              <option value="salvation">Salvation</option>
              <option value="healing">Healing</option>
              <option value="family">Family</option>
              <option value="youth">Youth</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <AdminLoginLabel htmlFor="new-status">Status</AdminLoginLabel>
            <select
              id="new-status"
              name="status"
              value={newPost.status}
              onChange={handleNewPostChange}
              style={{ width: '100%', padding: 6, fontSize: '0.85rem' }}
            >
              <option value="published">Published</option>
            </select>
          </div>
        </div>
        <div style={{ marginBottom: 8 }}>
          <label style={{ fontSize: '0.85rem' }}>
            <input
              type="checkbox"
              name="isFeatured"
              checked={newPost.isFeatured}
              onChange={handleNewPostChange}
              style={{ marginRight: 6 }}
            />
            Mark as featured
          </label>
        </div>
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: '16px' }}>
          <AdminSaveButton type="submit" disabled={savingNewPost}>
            {savingNewPost ? 'Saving...' : 'Create Testimony'}
          </AdminSaveButton>
          {postsSaveSuccess && !editingPost && (
            <span style={{ color: '#1b5e20', fontWeight: '500' }}>{postsSaveSuccess}</span>
          )}
          {postsError && !editingPost && (
            <span style={{ color: '#b71c1c', fontWeight: '500' }}>{postsError}</span>
          )}
        </div>
      </form>

      <hr style={{ margin: '16px 0' }} />

      <AdminSectionTitle>Existing Testimonies</AdminSectionTitle>
      {postsLoading && <AdminSectionText>Loading testimonies...</AdminSectionText>}
      {!postsLoading && posts.length === 0 && (
        <AdminSectionText>No testimonies found yet.</AdminSectionText>
      )}

      {!postsLoading && posts.length > 0 && (
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            {posts.map((postItem) => (
              <div
                key={postItem._id}
                style={{
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  padding: 10,
                  marginBottom: 8,
                  fontSize: '0.9rem',
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{postItem.title}</div>
                <div style={{ fontSize: '0.8rem', marginBottom: 4 }}>
                  {postItem.author && <span>{postItem.author}</span>}
                  {postItem.author && postItem.status && <span> • </span>}
                  {postItem.status && <span>{postItem.status}</span>}
                  {postItem.isFeatured && <span> • Featured</span>}
                </div>
                {postItem.image && (
                  <div style={{ marginBottom: 6 }}>
                    <Base64Image
                      image={postItem.image}
                      alt={postItem.title || 'Testimony image'}
                      style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }}
                    />
                  </div>
                )}
                <div style={{ marginBottom: 6 }}>
                  {(postItem.description || '').slice(0, 120)}
                  {postItem.description && postItem.description.length > 120 ? '…' : ''}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <AdminButton
                    type="button"
                    onClick={() => startEditing(postItem)}
                    style={{ fontSize: '0.8rem', padding: '4px 10px' }}
                  >
                    Edit
                  </AdminButton>
                  <AdminButton
                    type="button"
                    onClick={() => deletePost(postItem._id)}
                    style={{
                      fontSize: '0.8rem',
                      padding: '4px 10px',
                      borderColor: '#b71c1c',
                      color: '#b71c1c',
                    }}
                  >
                    Delete
                  </AdminButton>
                  <label
                    style={{
                      fontSize: '0.8rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <AdminButton
                      type="button"
                      style={{ fontSize: '0.8rem', padding: '4px 10px' }}
                    >
                      {imageUploadingId === postItem._id ? 'Uploading…' : 'Upload Image'}
                    </AdminButton>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        e.target.files &&
                        e.target.files[0] &&
                        handleImageUpload(postItem._id, e.target.files[0])
                      }
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>

          {editingPost && (
            <div style={{ flex: 1, border: '1px solid #ddd', borderRadius: 8, padding: 12 }}>
              <AdminSectionTitle style={{ fontSize: '1rem' }}>
                Edit Testimony
              </AdminSectionTitle>
              <form onSubmit={saveEdit}>
                <div style={{ marginBottom: 8 }}>
                  <AdminLoginLabel htmlFor="edit-title">Title</AdminLoginLabel>
                  <AdminLoginInput
                    id="edit-title"
                    name="title"
                    value={editingPost.title || ''}
                    onChange={handleEditChange}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <AdminLoginLabel htmlFor="edit-author">Author</AdminLoginLabel>
                  <AdminLoginInput
                    id="edit-author"
                    name="author"
                    value={editingPost.author || ''}
                    onChange={handleEditChange}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <AdminLoginLabel htmlFor="edit-description">Description</AdminLoginLabel>
                  <textarea
                    id="edit-description"
                    name="description"
                    rows={4}
                    value={editingPost.description || ''}
                    onChange={handleEditChange}
                    style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <AdminLoginLabel htmlFor="edit-category">Category</AdminLoginLabel>
                    <select
                      id="edit-category"
                      name="category"
                      value={editingPost.category || 'other'}
                      onChange={handleEditChange}
                      style={{ width: '100%', padding: 6, fontSize: '0.85rem' }}
                    >
                      <option value="salvation">Salvation</option>
                      <option value="healing">Healing</option>
                      <option value="family">Family</option>
                      <option value="youth">Youth</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <AdminLoginLabel htmlFor="edit-status">Status</AdminLoginLabel>
                    <select
                      id="edit-status"
                      name="status"
                      value={editingPost.status || 'published'}
                      onChange={handleEditChange}
                      style={{ width: '100%', padding: 6, fontSize: '0.85rem' }}
                    >
                      <option value="published">Published</option>
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontSize: '0.85rem' }}>
                    <input
                      type="checkbox"
                      name="isFeatured"
                      checked={!!editingPost.isFeatured}
                      onChange={handleEditChange}
                      style={{ marginRight: 6 }}
                    />
                    Mark as featured
                  </label>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <AdminSaveButton type="submit" disabled={savingEdit}>
                    {savingEdit ? 'Saving...' : 'Save Changes'}
                  </AdminSaveButton>
                  <AdminButton type="button" onClick={() => setEditingPost(null)}>
                    Cancel
                  </AdminButton>
                  {postsSaveSuccess && editingPost && (
                    <span style={{ color: '#1b5e20', fontWeight: '500' }}>{postsSaveSuccess}</span>
                  )}
                  {postsError && editingPost && (
                    <span style={{ color: '#b71c1c', fontWeight: '500' }}>{postsError}</span>
                  )}
                </div>
              </form>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AdminTestimoniesPage;
